import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreContext';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeColor } from '../../hooks/useThemeColor';
import { isAppShell } from '../../marketing/utils/isInstalledApp';
import StoreCard from '../../components/StoreCard/StoreCard';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import invitationService, { Invitation } from '../../services/invitation.service';
import '../../components/AuthGate/AuthGate.scss';
import './StoreSelection.scss';

const StoreSelection: React.FC = () => {
  useThemeColor('#6B21A8');
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { stores, getUserStores, selectStore, selectStoreByStore, isLoading, error } = useStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const home = isAppShell() ? '/stores' : '/';

  useEffect(() => {
    getUserStores().catch(err => {
      console.error('Failed to load stores:', err);
    });

    loadPendingInvitations();
  }, []);

  const loadPendingInvitations = async () => {
    try {
      const invitations = await invitationService.getPendingInvitations();
      setPendingInvitations(invitations);
    } catch (err: any) {
      console.error('Failed to load pending invitations:', err);
    }
  };

  const handleAcceptInvitation = async (invitationId: number) => {
    try {
      const result = await invitationService.acceptInvitation(invitationId);
      const updatedStores = await getUserStores();
      await loadPendingInvitations();

      const store = updatedStores.find(s => s.id === result.storeId);
      if (store) {
        selectStoreByStore(store);
      } else {
        selectStore(result.storeId);
      }
      navigate('/orders');
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      alert(err.response?.data?.error?.message || 'Échec de l\'acceptation');
    }
  };

  const handleRejectInvitation = async (invitationId: number) => {
    if (!window.confirm('Refuser cette invitation ?')) return;

    try {
      await invitationService.rejectInvitation(invitationId);
      await loadPendingInvitations();
    } catch (err: any) {
      console.error('Failed to reject invitation:', err);
      alert(err.response?.data?.error?.message || 'Échec du refus');
    }
  };

  const handleStoreClick = (storeId: number) => {
    selectStore(storeId);
    navigate('/orders');
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/login');
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase() || user.pseudo?.charAt(0)?.toUpperCase() || 'U';
  };

  if (isLoading && stores.length === 0) {
    return (
      <div className="store-deck">
        <div className="store-deck__loading">
          <div className="store-deck__spin" />
          <p>Chargement…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="store-deck">
      {showUserMenu && (
        <div
          className="store-deck__backdrop"
          onClick={() => setShowUserMenu(false)}
          aria-hidden="true"
        />
      )}

      <header className="store-deck__bar">
        <Link to={home} className="store-deck__brand">
          <img src="/favicon.svg" alt="" />
          Zeedcom
        </Link>
        <div className="store-deck__user">
          <button
            type="button"
            className="store-deck__user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-expanded={showUserMenu}
            aria-haspopup="menu"
          >
            <span className="store-deck__avatar">{getUserInitials()}</span>
            <span className="store-deck__uname">
              {user?.first_name} {user?.last_name}
            </span>
            <Icon name="chevron-right" size={14} />
          </button>

          {showUserMenu && (
            <div className="store-deck__menu" role="menu">
              <div className="store-deck__menu-head">
                <span className="store-deck__avatar">{getUserInitials()}</span>
                <div>
                  <strong>{user?.first_name} {user?.last_name}</strong>
                  <span>{user?.email}</span>
                </div>
              </div>
              <button type="button" role="menuitem" onClick={handleLogout}>
                Déconnexion
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="store-deck__main">
        <p className="store-deck__eye">Stores</p>
        <h1 className="store-deck__title">Choisir un store</h1>
        <p className="store-deck__lead">Ouvrez-en un, ou préparez-le avec l'assistant.</p>

        {error && (
          <div className="auth-alert auth-alert--error">{error}</div>
        )}

        {pendingInvitations.length > 0 && (
          <section className="store-deck__invites">
            <h2>Invitations ({pendingInvitations.length})</h2>
            <ul>
              {pendingInvitations.map(invitation => (
                <li key={invitation.id}>
                  <div>
                    <strong>{invitation.store_name}</strong>
                    <span>
                      {invitation.invited_by_pseudo} · {invitation.role_name}
                    </span>
                  </div>
                  <div className="store-deck__invite-act">
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => handleAcceptInvitation(invitation.id)}
                    >
                      Accepter
                    </Button>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => handleRejectInvitation(invitation.id)}
                    >
                      Refuser
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {stores.length === 0 && pendingInvitations.length === 0 ? (
          <div className="store-deck__empty">
            <Icon name="store" size={28} />
            <h2>Aucun store</h2>
            <p>Préparez-en un avec l'assistant, ou attendez une invitation.</p>
          </div>
        ) : (
          <div className="store-deck__grid">
            {stores.map(store => (
              <StoreCard
                key={store.id}
                store={store}
                onClick={() => handleStoreClick(store.id)}
              />
            ))}
          </div>
        )}

        <div className="store-deck__act">
          <Button variant="primary" size="large" onClick={() => navigate('/stores/create')}>
            <Icon name="sparkles" size={16} /> Avec l'assistant
          </Button>
          <Button variant="outline" size="large" onClick={() => navigate('/stores/create-from-template')}>
            <Icon name="layers" size={16} /> Depuis un modèle
          </Button>
        </div>
      </main>
    </div>
  );
};

export default StoreSelection;
